import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  LinearProgress,
  Alert,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import apiService from '../../services/api';
import { createLogger, adminLogContext } from '../../utils/logger';
import { addUserActionBreadcrumb } from '../../utils/sentryTracking';

const logger = createLogger('ReportPublishing');
const ctx = (extra) => adminLogContext('report_publishing', extra);

const CONFIRM_PHRASE = 'confirm publish changes';

const STATUS_COLORS = {
  published: 'success',
  draft: 'warning',
  unpublished: 'default',
  orphaned: 'error',
};

const ReportPublishing = () => {
  const [counties, setCounties] = useState([]);
  const [selectedCountyId, setSelectedCountyId] = useState('');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [stagedPublish, setStagedPublish] = useState(new Set());
  const [stagedUnpublish, setStagedUnpublish] = useState(new Set());

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmInput, setConfirmInput] = useState('');

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailEntry, setDetailEntry] = useState(null);

  useEffect(() => {
    const loadCounties = async () => {
      try {
        const data = await apiService.getCountyDocumentStatus();
        const sorted = (data || [])
          .map((c) => ({ id: c.county_id, name: c.full_name || `${c.name}, ${c.state}` }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setCounties(sorted);
      } catch (err) {
        logger.apiFailure('Failed to load counties', ctx({ action: 'load_counties' }), err);
      }
    };
    loadCounties();
  }, []);

  const loadReports = async (countyId) => {
    if (!countyId) {
      setEntries([]);
      return;
    }
    setLoading(true);
    setError('');
    const t0 = performance.now();
    try {
      const data = await apiService.getReportsForReview({ countyId });
      setEntries(data.entries || []);
      setStagedPublish(new Set());
      setStagedUnpublish(new Set());
      logger.info('Reports for review loaded', ctx({
        action: 'load_review',
        county_id: countyId,
        count: (data.entries || []).length,
        load_time_ms: Math.round(performance.now() - t0),
      }));
    } catch (err) {
      logger.apiFailure('Failed to load reports for review', ctx({ action: 'load_review' }), err);
      setError(err?.message || 'Failed to load reports.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReports(selectedCountyId);
  }, [selectedCountyId]);

  const stats = useMemo(() => {
    const s = { draft: 0, published: 0, unpublished: 0, orphaned: 0, total: entries.length };
    for (const e of entries) {
      if (s[e.publish_status] !== undefined) s[e.publish_status]++;
    }
    return s;
  }, [entries]);

  const filteredEntries = useMemo(() => {
    let list = entries;
    if (statusFilter) {
      list = list.filter((e) => e.publish_status === statusFilter);
    }
    const q = filter.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (e) =>
          (e.case_number || '').toLowerCase().includes(q) ||
          (e.one_sentence_summary || '').toLowerCase().includes(q),
      );
    }
    return list;
  }, [entries, filter, statusFilter]);

  const handleToggle = (entry) => {
    const entryId = entry.id;
    const pubId = entry.published_report_id;

    if (entry.publish_status === 'draft') {
      setStagedPublish((prev) => {
        const next = new Set(prev);
        if (next.has(entryId)) next.delete(entryId);
        else next.add(entryId);
        return next;
      });
    } else if (entry.publish_status === 'published' && pubId) {
      setStagedUnpublish((prev) => {
        const next = new Set(prev);
        if (next.has(pubId)) next.delete(pubId);
        else next.add(pubId);
        return next;
      });
    } else if (entry.publish_status === 'unpublished' && pubId) {
      setStagedPublish((prev) => {
        const next = new Set(prev);
        if (next.has(entryId)) next.delete(entryId);
        else next.add(entryId);
        return next;
      });
    }
  };

  const isStaged = (entry) => {
    if (stagedPublish.has(entry.id)) return true;
    if (entry.published_report_id && stagedUnpublish.has(entry.published_report_id)) return true;
    return false;
  };

  const stagedCount = stagedPublish.size + stagedUnpublish.size;
  const hasStaged = stagedCount > 0;
  const confirmOk = confirmInput.trim().toLowerCase() === CONFIRM_PHRASE.toLowerCase();

  const stagePublishAllDrafts = () => {
    const draftIds = entries
      .filter((e) => e.publish_status === 'draft' && e.id)
      .map((e) => e.id);
    setStagedPublish(new Set(draftIds));
  };

  const discardStaged = () => {
    setStagedPublish(new Set());
    setStagedUnpublish(new Set());
    setError('');
  };

  const openConfirm = () => {
    setConfirmInput('');
    setConfirmOpen(true);
  };

  const applyStaged = async () => {
    if (!confirmOk || !hasStaged) return;
    setApplying(true);
    setError('');
    addUserActionBreadcrumb('Apply report publish changes', {
      view: 'admin_report_publishing',
      action: 'apply_publish_changes',
      staged_count: stagedCount,
    });
    try {
      if (stagedPublish.size > 0) {
        await apiService.publishReports([...stagedPublish]);
      }
      for (const pubId of stagedUnpublish) {
        await apiService.unpublishReport(pubId);
      }
      setConfirmOpen(false);
      setConfirmInput('');
      await loadReports(selectedCountyId);
    } catch (err) {
      logger.apiFailure('Failed to apply publish changes', ctx({
        action: 'apply_publish_changes',
        staged_count: stagedCount,
      }), err);
      setError(err?.message || 'Failed to apply changes. Reloading from server.');
      await loadReports(selectedCountyId);
    } finally {
      setApplying(false);
    }
  };

  const handleRowClick = (entry) => {
    setDetailEntry(entry);
    setDetailOpen(true);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h5" gutterBottom>
        Report Publishing
      </Typography>
      {loading && <LinearProgress sx={{ mb: 1 }} />}
      <Typography variant="body2" sx={{ mb: 2, maxWidth: 820, opacity: loading ? 0.5 : 1, transition: 'opacity 0.2s' }}>
        Select a county to review its report entries. Toggle individual reports or publish all drafts
        in batch. Changes are <strong>staged</strong> until you confirm with the phrase below.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel>County</InputLabel>
          <Select
            value={selectedCountyId}
            label="County"
            onChange={(e) => setSelectedCountyId(e.target.value)}
          >
            <MenuItem value="">— Select county —</MenuItem>
            {counties.map((c) => (
              <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="published">Published</MenuItem>
            <MenuItem value="unpublished">Unpublished</MenuItem>
            <MenuItem value="orphaned">Orphaned</MenuItem>
          </Select>
        </FormControl>

        <TextField
          size="small"
          placeholder="Filter by case number or summary…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          sx={{ minWidth: 240 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      {selectedCountyId && !loading && (
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
          <Chip label={`${stats.draft} draft${stats.draft !== 1 ? 's' : ''}`} color="warning" variant="outlined" size="small" />
          <Chip label={`${stats.published} published`} color="success" variant="outlined" size="small" />
          {stats.unpublished > 0 && <Chip label={`${stats.unpublished} unpublished`} variant="outlined" size="small" />}
          {stats.orphaned > 0 && <Chip label={`${stats.orphaned} orphaned`} color="error" variant="outlined" size="small" />}
          <Chip label={`${stats.total} total`} variant="outlined" size="small" />
        </Stack>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2, flexWrap: 'wrap' }}>
        <Chip
          label={hasStaged ? `${stagedCount} staged change${stagedCount === 1 ? '' : 's'}` : 'No staged changes'}
          color={hasStaged ? 'warning' : 'default'}
          variant={hasStaged ? 'filled' : 'outlined'}
        />
        <Button
          variant="outlined"
          size="small"
          disabled={!selectedCountyId || stats.draft === 0 || applying}
          onClick={stagePublishAllDrafts}
        >
          Stage publish all drafts
        </Button>
        <Button variant="outlined" size="small" disabled={!hasStaged || applying} onClick={discardStaged}>
          Discard staged
        </Button>
        <Button variant="contained" size="small" disabled={!hasStaged || applying} onClick={openConfirm}>
          Review &amp; apply
        </Button>
      </Stack>

      {selectedCountyId && (
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 560 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, width: 60 }}>Stage</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Case Number</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Summary</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Categories</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Publish</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Version</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEntries.map((entry) => {
                const staged = isStaged(entry);
                const isPublishedOrStaged =
                  entry.publish_status === 'published'
                    ? !stagedUnpublish.has(entry.published_report_id)
                    : stagedPublish.has(entry.id);
                return (
                  <TableRow
                    key={entry.id || entry.published_report_id}
                    hover
                    sx={{
                      cursor: 'pointer',
                      bgcolor: staged ? 'warning.50' : undefined,
                    }}
                    onClick={() => handleRowClick(entry)}
                  >
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      {entry.publish_status !== 'orphaned' && (
                        <Tooltip title={isPublishedOrStaged ? 'Will be published' : 'Will be unpublished/draft'}>
                          <Switch
                            size="small"
                            checked={isPublishedOrStaged}
                            disabled={applying}
                            onChange={() => handleToggle(entry)}
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                      {entry.case_number || '—'}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {entry.one_sentence_summary || '—'}
                    </TableCell>
                    <TableCell>
                      {(entry.category_names || []).slice(0, 2).map((cat) => (
                        <Chip key={cat} label={cat} size="small" sx={{ mr: 0.5, mb: 0.5, fontSize: '0.7rem' }} />
                      ))}
                    </TableCell>
                    <TableCell>
                      <Chip label={entry.current_status || '—'} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={staged ? `${entry.publish_status} → staged` : entry.publish_status}
                        size="small"
                        color={STATUS_COLORS[entry.publish_status] || 'default'}
                        variant={staged ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {entry.version || '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
              {filteredEntries.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {selectedCountyId ? 'No reports match the current filters.' : 'Select a county to begin.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Confirmation dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => !applying && setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: 1800 }}
      >
        <DialogTitle>Apply staged publish changes</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            You are about to <strong>publish {stagedPublish.size}</strong> report(s) and{' '}
            <strong>unpublish {stagedUnpublish.size}</strong> report(s).
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Type the following phrase exactly:
          </Typography>
          <Paper variant="outlined" sx={{ px: 2, py: 1, mb: 2, fontFamily: 'monospace', fontSize: '0.95rem' }}>
            {CONFIRM_PHRASE}
          </Paper>
          <TextField
            fullWidth
            autoFocus
            label="Confirmation"
            placeholder={CONFIRM_PHRASE}
            value={confirmInput}
            onChange={(e) => setConfirmInput(e.target.value)}
            disabled={applying}
            error={confirmInput.length > 0 && !confirmOk}
            helperText={confirmInput.length > 0 && !confirmOk ? 'Phrase does not match' : undefined}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={applying}>
            Cancel
          </Button>
          <Button variant="contained" onClick={applyStaged} disabled={!confirmOk || applying}>
            {applying ? 'Applying…' : 'Apply changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Report detail preview dialog */}
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="md"
        fullWidth
        sx={{ zIndex: 1800 }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{detailEntry?.case_number || 'Report Detail'}</span>
          {detailEntry && (
            <Chip
              label={detailEntry.publish_status}
              size="small"
              color={STATUS_COLORS[detailEntry.publish_status] || 'default'}
            />
          )}
        </DialogTitle>
        <DialogContent dividers>
          {detailEntry && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="overline" color="text.secondary">Summary</Typography>
                <Typography variant="body1">{detailEntry.one_sentence_summary || '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="overline" color="text.secondary">Categories</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {(detailEntry.category_names || []).map((cat) => (
                    <Chip key={cat} label={cat} size="small" />
                  ))}
                </Box>
              </Box>
              <Box>
                <Typography variant="overline" color="text.secondary">Current Status</Typography>
                <Typography variant="body2">{detailEntry.current_status || '—'}</Typography>
              </Box>
              <Stack direction="row" spacing={4}>
                <Box>
                  <Typography variant="overline" color="text.secondary">First Activity</Typography>
                  <Typography variant="body2">{detailEntry.first_activity_date || '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="overline" color="text.secondary">Last Activity</Typography>
                  <Typography variant="body2">{detailEntry.last_activity_date || '—'}</Typography>
                </Box>
              </Stack>
              {detailEntry.published_at && (
                <Stack direction="row" spacing={4}>
                  <Box>
                    <Typography variant="overline" color="text.secondary">Published At</Typography>
                    <Typography variant="body2">{new Date(detailEntry.published_at).toLocaleString()}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="overline" color="text.secondary">Version</Typography>
                    <Typography variant="body2">{detailEntry.version || '—'}</Typography>
                  </Box>
                </Stack>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReportPublishing;
